import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';

// Shared Components and Helpers
import { NgForm } from '@angular/forms';
import { Alert } from '../../../../shared/alert.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageType } from '../../../../shared/messageType';
import { getAlertMessage } from '../../../../shared/api-loaders';
import { Select2Option } from '../../../../shared/select2-option';
import { CascadeManager } from '../../../../shared/cascade-manager';

// Services
import { ModalService } from '../../../../shared/modal.service';
import { MessageService } from '../../../../shared/message.service';
import { SecurityService } from '../../../../shared/security.service';
import { ProfileSyncService } from '../../../services/profile-sync.service';
import { CustomerService } from '../../../../ect-feature/services/customer.service';
import { ImportUsersService } from "../../../services/import-users.service";
// Models
import { MigrationUser } from '../../../model/migration-user';
import { RolesDisciplines } from '../../../../users/models/user-detail-options';
import { ImportUsersData } from '../../../../conversions-feature/model/Import-users';
import { getDefaultDataTableDefinitionLocalProcessing } from '../../../../shared/DataTablesHelper';
import { ImportConfirmationDialogComponent } from '../import-confirmation-dialog/import-confirmation-dialog.component';

@Component({
    selector: 're-import-list',
    templateUrl: './import-list.component.html',
    styleUrls: ['./import-list.component.scss'],
    standalone: false
})
export class ImportListComponent {

  @ViewChild("form")
  form: NgForm;

  @ViewChild('file', {static: false}) file;

  selectedFile: File;
  running = false;
  uploading = false;
  fileName = '';

  // Data
  referenceData: RolesDisciplines[];
  // Table Data
  unfilteredData: MigrationUser[] = null;
  results: MigrationUser[] = [];
  dataTable: DataTables.Api;

  // Dropdowns
  customer: number = null;
  environment: number = null;

  customers: Select2Option[] = [];
  environments: Select2Option[] = [];

  // Miscellaneous
  onlyErrors = false;
  updateExistingUsers = false;
  numberOfErrors: number;
  errorPercentage: number;
  isImportUsers: boolean;
  domain: string;
  domainRegExPattern = /@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/i;

  // Helpers
  alert: Alert;
  cascadeManager = new CascadeManager(this);

  // Source
  selectedSource: string;
  // sources = [{name : "CSV"}, {name : "Active Directory"}, {name :  "API Connector"}];
  sources = [{name : "CSV"}];
  importUsersValid: boolean = false;

  // Upload CSV return value
  userCount: number = 0;
  errorUserCount: number;

  get exportToCsvButton(): Element {
    return this.elementRef.nativeElement.querySelector(".button-container .buttons-csv");
  }

  constructor(private elementRef: ElementRef, private renderer: Renderer2, private route: ActivatedRoute, private router: Router,
    private messageService: MessageService,
    private customerService: CustomerService, private importUsersService: ImportUsersService,
    private profileSyncService: ProfileSyncService, private modalService: ModalService, private securityService: SecurityService) {
    this.cascadeManager.defaultResetAction = () => { this.alert = undefined; };
    this.cascadeManager.addEvent({ event: this.customerChanged,    reset: () => { this.customers = []; this.customer = null; this.referenceData = []; } });
    this.cascadeManager.addEvent({ event: this.environmentChanged, reset: () => { this.environments = []; this.environment = null; } });
    this.cascadeManager.addEvent({ event: null, reset: () => { this.unfilteredData = null; } });
    this.selectedSource = this.sources[0].name;
  }

  async ngOnInit() {
    if (await this.securityService.isAuthenticated()) {
      this.isImportUsers = await this.securityService.isImportUsers().toPromise();
    }
    this.load();
    this.configDataTable();
  }

  // Load customers only 
  // Environments will be populated from reference data API when customer is selected
  private async load(): Promise<void> {
    this.showSpinner();

    this.customerService.getCustomersByStack().subscribe(customers => {
      this.customers = customers.map(c => { return { data: c.accountId, id: c.id.toString(), text: c.accountName } });
      this.hideSpinner();
    }, error => {
      this.hideSpinner();
      this.alert = getAlertMessage(error);
    });

  }

  ngAfterViewInit(): void {
    this.reset();
  }

  export() {  }

  reset() {
    this.form.resetForm({});
    this.environments = [];
    this.referenceData = [];
    this.results = null;
    this.unfilteredData = null;
    this.dataTable?.clear();
    this.dataTable?.draw();
    this.updateExistingUsers = false;
    this.cascadeManager.runFirstEvent();
    this.calculateNumberOfErrors(null);
    this.importUsersValid = false;
    this.userCount = 0;
    this.errorUserCount = null;
    if((<HTMLInputElement>$("#templatesTree .treeview-header .row-filter input")[ 0 ])) {
      (<HTMLInputElement>$("#templatesTree .treeview-header .row-filter input")[ 0 ]).value = "";
    }
  }

  customerChanged($event): void {
    this.cascadeManager.resetDependencies(this.customerChanged);
    this.updateExistingUsers = false;

    if (this.customer) {
      this.showSpinner();
      let crmId = this.customers.firstOrDefault(x => +x.id == this.customer).data;
      this.customerService.getCorporateStructureByCrmId(crmId).subscribe(x => {
        this.referenceData = x;

        // Populate environments from reference data instead of global configurations
        this.environments = this.referenceData
          .map<Select2Option>(e => ({ id: e.configId.toString(), text: e.configName }))
          .filter((env, index, self) =>
            index === self.findIndex((t) => t.id === env.id)
          );

        if (this.environments.length > 0) { this.environment = +this.environments[0].id; }

        this.cascadeManager.runNextDependendentEvent(this.customerChanged);
        this.hideSpinner();
      }, error => {
        this.hideSpinner();
        this.alert = getAlertMessage(error);
      });
    } else {
      this.hideSpinner();
      this.cascadeManager.runNextDependendentEvent(this.customerChanged);
    }
  }

  environmentChanged($event): void {
    this.showSpinner();
    this.cascadeManager.resetDependencies(this.environmentChanged);
    this.updateExistingUsers = false;
    this.cascadeManager.runNextDependendentEvent(this.environmentChanged);
    this.hideSpinner();
  }

  getCurrentEnvironment(): RolesDisciplines | null {
    let currentEnvironment = this.referenceData.firstOrDefault(x => +x.configId == this.environment);
    if (currentEnvironment == null) {
      console.error(`Environment ${this.environment} not found.`);
      return null;
    }
    return currentEnvironment;
  }

  applyExtraFilter() {
    this.results = this.unfilteredData;
    this.dataTable.clear();
    if(this.results) {
      this.dataTable.rows.add(this.results);
      this.dataTable.draw();
    }
  }

  enableDisableExportButton() {
    if (this.dataTable.page.info().recordsDisplay == 0)
      this.disableExportButton();
    else
      this.enableExportButton();
  }

  disableExportButton() {
    this.renderer.setAttribute(this.exportToCsvButton, "disabled", "true");
  }

  enableExportButton() {
    this.renderer.removeAttribute(this.exportToCsvButton, "disabled");
  }

  configDataTable() {
    window['currentComponent'] = this;
    this.dataTable = getDefaultDataTableDefinitionLocalProcessing("#resultsDataTable", {
      buttons: [
        {
          extend: 'csv', text: 'Export to CSV', className:'btn btn-primary'
        }
      ],
      columns: [
        {
          title: 'User Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.userName}`;
          }
        },
        {
          title: 'First Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.firstName}`;
          }
        },
        {
          title: 'Middle Initial', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.middleName ?? ""}`;
          }
        },
        {
          title: 'Last Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.lastName}`;
          }
        },
        {
          title: 'Email', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.email ?? ""}`;
          }
        },
        {
          title: 'Password Type', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.passwordType}`;
          }
        },
        {
          title: 'Validation Status', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.notes}`;
          }
        },
      ]
    });

    new $.fn.dataTable[ 'FixedHeader' ](this.dataTable);
    this.dataTable.buttons().container().appendTo($('.button-container'));

    this.dataTable.on('search.dt', () => {
      this.enableDisableExportButton();
    });
  }


  run() {
    console.log('form data', this.form.value);
  }

  async import() {
    let dialog = this.modalService.create<ImportConfirmationDialogComponent>(ImportConfirmationDialogComponent);
    dialog.component.numberOfUsers = this.userCount;
    dialog.component.crmID = this.customers.firstOrDefault(x => +x.id == this.customer).data;
    dialog.component.domain = this.domain;

    let result = await dialog.modalRef.result;
    if (result) {
      this.importUsers(dialog.component.domain, dialog.component.crmID);
    }
  }

  importUsers(domain:string, crmID:string) {
    this.showSpinner();
    let importUsers : ImportUsersData = {
      crmId : crmID,
      // envId : this.environment,
      importUsersCSV: this.selectedFile,
      source: this.selectedSource,
      updateExistingUsers: this.updateExistingUsers,
      customerEmailDomain: domain,
      // environmentName: this.environments.firstOrDefault(x => +x.id == this.environment).text
    };
    this.importUsersService.import(importUsers).subscribe(
      (event: any) => {
      if(event.type !== 0) {
        this.alert = undefined;
        this.results = [];
        this.hideSpinner();
        setTimeout(()=> this.back(), 1000);
      }
    }, (error: any) => {
      this.hideSpinner();
      this.alert = getAlertMessage(error);
    });
  }

  private showSpinner(): void {
    this.messageService.send(MessageType.ProcessingOn);
  }

  private hideSpinner(): void {
    this.messageService.send(MessageType.ProcessingOff);
  }

  back() {
    this.router.navigate([".."], { relativeTo: this.route })
  }

  ngOnDestroy(): void {
    delete window['currentComponent'];
    this.dataTable.destroy();
  }

  downloadSampleTemplate() {
    this.importUsersService.downloadSampleTemplate();
  }

  downloadPhysicianSampleTemplate() {
    this.importUsersService.downloadPhysicianSampleTemplate();
  }

  onFileSelected() {
    // clear everything on new file selection
    this.messageService.clearAlert();
    this.unfilteredData = null;
    this.selectedFile = null;
    this.alert = null;
    this.fileName = null;
    this.importUsersValid = false;
    this.calculateNumberOfErrors(null);
    this.errorUserCount = null;
    this.selectedFile = this.file.nativeElement.files[0];
    if (this.selectedFile) {
      this.fileName = this.selectedFile.name;
      this.validateFileType();
    }
  }

  selectFile() {
    this.file.nativeElement.click();
  }

  validateFileType(): void {
    if(this.selectedFile && this.selectedFile.type === 'text/csv') {
      this.form.form.setErrors(null);
      this.calculateNumberOfErrors(null);
      this.applyExtraFilter();
      this.enableDisableExportButton();
    } else {
      this.form.form.setErrors({ invalidFileType: true });
    }
  }

  private calculateNumberOfErrors(data) {
    if(data) {
      if(data.body) {
        this.unfilteredData = data.body.users;
        this.userCount = data.body.userCount;
        this.numberOfErrors = data.body.errorUserCount;
        this.errorPercentage = (this.numberOfErrors / this.userCount) * 100;
      } else {
        this.unfilteredData = null;
        this.userCount = data ? data.body.userCount : 0;
        this.numberOfErrors = 0;
        this.errorPercentage = 0;
      }
    }
  }

  parseCSV() {
    this.showSpinner();
    this.errorPercentage = 0;
    this.importUsersService.uploadUsers(this.domain, this.selectedFile).subscribe((data: any) => {
      if(data && data.body) {
        if(data.body.users && data.body.users.length > 0) {
          // show button even on error users
          this.importUsersValid = true;
          this.calculateNumberOfErrors(data);
          this.applyExtraFilter();
          this.enableDisableExportButton();
          this.hideSpinner();
        } else {
          this.importUsersValid = false;
          this.calculateNumberOfErrors(data);
          this.applyExtraFilter();
          this.errorUserCount = 0;
          this.hideSpinner();
        }
      }
    }, e => {
      this.importUsersValid = false;
      this.errorPercentage = 0;
      this.hideSpinner();
      this.messageService.sendError(getAlertMessage(e).message);
    });
  }

}
